import { FeasibilityCriterion, FeasibilityResult } from '@ps/survey-utils';

/**
 * Analysis of FeasibilityResult: the total, a quota's feasible count,
 * whether it's difficult, and whether a qualification/criterion was
 * excluded from the calculation (UNKN).
 */
export class FeasibilityAnalysis {
  private constructor(
    private readonly _feasibility: FeasibilityResult,
    private readonly _unusedCriteria: Record<string, string[]>,
  ) {
    this._feasibility.unusedCriteria.forEach((unused) => {
      Object.entries(unused).forEach(([qualificationCode, ids]) => {
        this._unusedCriteria[qualificationCode] = [
          ...new Set([
            ...(this._unusedCriteria[qualificationCode] ?? []),
            ...ids,
          ]),
        ];
      });
    });
  }

  public static from(feasibility: FeasibilityResult): FeasibilityAnalysis {
    const unusedCriteria: Record<string, string[]> = {};

    feasibility.unusedCriteria.forEach((unused) => {
      Object.entries(unused).forEach(([qualificationCode, ids]) => {
        unusedCriteria[qualificationCode] = [
          ...new Set([...(unusedCriteria[qualificationCode] ?? []), ...ids]),
        ];
      });
    });

    return new FeasibilityAnalysis(feasibility, unusedCriteria);
  }

  public get total(): number {
    return this._feasibility.totalFeasibility;
  }

  public count(buyerQuotaId: string): number {
    return (
      this._feasibility.quotas.find(
        (quota) => quota.buyerQuotaId === buyerQuotaId,
      )?.count ?? 0
    );
  }

  public isDifficult(buyerQuotaId: string): boolean {
    return this._feasibility.difficultQuotaIds.includes(buyerQuotaId);
  }

  public hasPopulationData(qualificationCode: number): boolean {
    return !this._feasibility.unusedQualifications.includes(qualificationCode);
  }

  public isUnknown(
    qualificationCode: number,
    criterion: FeasibilityCriterion,
  ): boolean {
    return (
      this._feasibility.unusedQualifications.includes(qualificationCode) ||
      this._hasUnusedCondition(qualificationCode, criterion)
    );
  }

  /** Deduplicated, upper-cased qualification names — feeds the warning toast copy. */
  public unusedQualificationNames(
    criteria: readonly FeasibilityCriterion[],
  ): string[] {
    const names = new Set<string>();

    criteria.forEach((criterion) => {
      if (
        criterion.qualificationName &&
        this.isUnknown(criterion.qualificationCode, criterion)
      ) {
        names.add(criterion.qualificationName.toUpperCase());
      }
    });

    return [...names];
  }

  private _hasUnusedCondition(
    qualificationCode: number,
    { conditionCodes = [], rangeSets = [] }: FeasibilityCriterion,
  ): boolean {
    const unused = this._unusedCriteria[qualificationCode];
    if (!unused) {
      return false;
    }

    if (conditionCodes.length) {
      return conditionCodes.some((code) => unused.includes(code));
    }

    if (rangeSets.length) {
      return rangeSets.some(({ from, to }) => unused.includes(`${from}-${to}`));
    }

    return false;
  }
}
